import { SyntheticEvent } from 'react';
import {
  MasonryFormResponseData,
  postMasonryForm,
} from '@leagueplatform/masonry-api';
import { MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import { isStorybook } from '@leagueplatform/core';
import { captureError } from '@leagueplatform/observability';
import type {
  FieldNodeStateWithId,
  MasonryActionResult,
  MasonryEngineActionHandler,
  MasonryEngineNode,
} from '@leagueplatform/masonry-engine';
import {
  updateFieldNodesFromValidationResult,
  validateAndUpdateFieldNodes,
} from './form-validation-utils';
import { isNotSuccessValidationStatus } from '../../../utils/is-not-success-validation-status';
import {
  FORM_ERROR_BODY,
  FORM_ERROR_CTA_LABEL,
  FORM_ERROR_HEADING,
} from '../../../constants/form-error-modal-content';

const NODE_TYPES_WITH_LOADING_PROP = ['button', 'textAction'];
const NODE_TYPES_WITH_DISABLED_PROP = ['button'];

type ActionHandlerResources = Parameters<
  MasonryEngineActionHandler<MasonryDriverAction, MasonryDriverAction>
>[1];

/**
 * Toggle the loading or disabled state of a node that is invoking the form submission.
 * @param nodeId - The id of the node to toggle the state of.
 * @param resources - The resources of the action handler.
 * @param value - The value to set the state to.
 */
const toggleInvokingNodeState = async (
  nodeId: MasonryEngineNode['id'],
  { invokeAction, messagingController }: ActionHandlerResources,
  value: boolean,
) => {
  const nodeType = messagingController.sendMessage(nodeId, {
    type: 'getData',
    payload: {},
  }).type;

  if (NODE_TYPES_WITH_LOADING_PROP.includes(nodeType)) {
    await invokeAction({
      type: 'setNodeState',
      namespace: 'default',
      payload: { nodeId, state: { loading: value } },
    });
  }

  if (NODE_TYPES_WITH_DISABLED_PROP.includes(nodeType)) {
    await invokeAction({
      type: 'setNodeState',
      namespace: 'default',
      payload: { nodeId, state: { disabled: value } },
    });
  }
};

type SubmitFormWithValidationOptions<IsBlocking extends boolean> = {
  formId: string;
  formProcessorId: string;
  fieldNodes: FieldNodeStateWithId[];
  baseEndpoint?: string;
  namespace: string;
  ancestry: string[];
  event?: SyntheticEvent<Element, Event> | undefined;
  resources: ActionHandlerResources;
  isBlocking: IsBlocking;
  suppressErrorModal?: boolean;
};

export function submitFormWithValidation(
  options: SubmitFormWithValidationOptions<true>,
): Promise<MasonryActionResult<MasonryFormResponseData>>;
export function submitFormWithValidation(
  options: SubmitFormWithValidationOptions<false>,
): Promise<MasonryActionResult<Promise<MasonryFormResponseData>>>;
export function submitFormWithValidation(
  options: SubmitFormWithValidationOptions<boolean>,
): Promise<
  | MasonryActionResult<MasonryFormResponseData>
  | MasonryActionResult<Promise<MasonryFormResponseData>>
>;
export async function submitFormWithValidation({
  formId,
  formProcessorId,
  fieldNodes,
  baseEndpoint,
  namespace,
  ancestry,
  event,
  resources,
  isBlocking,
  suppressErrorModal = false,
}: SubmitFormWithValidationOptions<boolean>): Promise<
  | MasonryActionResult<MasonryFormResponseData>
  | MasonryActionResult<Promise<MasonryFormResponseData>>
> {
  const {
    messagingController,
    stateController,
    validationController,
    invokeAction,
  } = resources;

  const rootNodeId = ancestry.at(-1) as string;

  const getNodeQueryParams = messagingController.sendMessage(rootNodeId, {
    type: 'getQueryParams',
    payload: {},
  });

  const isClientSideValidationPassed = validateAndUpdateFieldNodes(
    fieldNodes,
    validationController,
    stateController,
    messagingController,
  );

  // If the client-side validation failed, return the error.
  if (!isClientSideValidationPassed) {
    return {
      isSuccess: false,
      error: new Error('Client-side validation failed'),
    };
  }

  // When we're in a Story, the submit api should not be called.
  if (isStorybook()) {
    return {
      isSuccess: true,
    };
  }

  const postPromise = postMasonryForm({
    formId,
    formProcessorId,
    fieldNodes,
    getNodeQueryParams,
    baseEndpoint,
  });

  if (!isBlocking) {
    return {
      isSuccess: true,
      data: postPromise,
    };
  }

  if (ancestry[0]) {
    // Set the loading or disabled state of the root node that invoked the form submission.
    await toggleInvokingNodeState(ancestry[0], resources, true);
  }

  try {
    const response = await postPromise;

    const {
      statusType,
      statusMessage,
      formFields: serverValidationResult,
    } = response.attributes;

    // If the server validation failed, update the field nodes with the server validation result.
    if (isNotSuccessValidationStatus(statusType)) {
      if (serverValidationResult) {
        // Update the field nodes with the server validation result.
        updateFieldNodesFromValidationResult(
          serverValidationResult,
          fieldNodes,
          validationController,
          stateController,
          messagingController,
          !!statusMessage, // hasFormLevelMessage
        );
      }

      if (statusMessage) {
        // Set the form status banner.
        invokeAction({
          type: 'setFormStatusBanner',
          namespace,
          ancestry,
          payload: {
            formNodeId: formId,
            statusMessage,
            statusType,
          },
        });
      }

      // Return the error.
      return {
        isSuccess: false,
        error: new Error(statusMessage || 'Server validation failed'),
      };
    }

    // Return the success response.
    return {
      isSuccess: true,
      data: response,
    };
  } catch (error) {
    captureError(error as Error);

    if (suppressErrorModal !== true && !stateController.instance.modalState) {
      await invokeAction({
        type: 'showModalWithProperties',
        namespace,
        ancestry,
        event,
        payload: {
          heading: FORM_ERROR_HEADING,
          body: FORM_ERROR_BODY,
          ctaLabel: FORM_ERROR_CTA_LABEL,
        },
      });
    }

    // Return the error.
    return {
      isSuccess: false,
      error,
    };
  } finally {
    if (ancestry[0]) {
      // Clear the loading or disabled state of the root node that invoked the form submission.
      await toggleInvokingNodeState(ancestry[0], resources, false);
    }
  }
}
